/**
 * Download Rides Hook
 * 
 * Custom hook for handling ride download operations with fetching, import,
 * progress tracking, and error handling.
 * 
 * @module useDownloadRides
 */

import { useState, useCallback } from 'react';
import { useRides } from '../context/RidesContext';
import { 
  fetchRides,
  fetchRidesDetailedBatch,
  type Environment,
  type UATRideEntry,
  type UATRideDetailedEntry,
} from '../services/fixedRouteUatClient';
import {
  extractUATRides,
  importUATRides,
  type RideImportResult,
} from '../services/fixedRouteUatImporter';
import { handleApiError } from '../utils/errorHandling';
import type { RouteOperationResult } from '../components/map/components/routes-transfer/OperationResults';
import type { Context } from '../context/context';
import { getEnvironmentDisplayName } from '../components/map/components/EnvironmentSelector';

/**
 * Download progress for a single ride
 */
export interface RideDownloadProgress {
  rideId: string;
  rideName: string;
  status: 'pending' | 'fetching' | 'importing' | 'success' | 'error';
  message: string;
  progress?: number; // 0-100
}

/**
 * Download state
 */
export interface DownloadState {
  /** Whether download is in progress */
  isDownloading: boolean;
  /** Whether validation is in progress */
  isValidating: boolean;
  /** Whether fetching is in progress */
  isFetching: boolean;
  /** Download progress for each ride */
  progress: Map<string, RideDownloadProgress>;
  /** Download results */
  results: RouteOperationResult[];
  /** Current error */
  error: string | null;
  /** Fetched rides */
  fetchedRides: (UATRideEntry | UATRideDetailedEntry)[];
  /** Layer name where rides were imported (for auto-selection) */
  importedLayerName?: string | null;
}

/**
 * Hook return type
 */
export interface UseDownloadRidesReturn {
  /** Download state */
  downloadState: DownloadState;
  /** Start download operation */
  startDownload: (
    selectedRideIds: Set<string>,
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkId: number | null;
      startDate: string;
      endDate: string;
      timezone?: string;
      downloadAcknowledged: boolean;
      environment: Environment;
      context?: Context; // Optional context for map layer integration
    }
  ) => Promise<void>;
  /** Reset download state */
  resetDownload: () => void;
}

/**
 * useDownloadRides Hook
 * 
 * Manages ride download operations with fetching, import, and progress tracking.
 */
export function useDownloadRides(): UseDownloadRidesReturn {
  const { bulkAddRides } = useRides();
  const [downloadState, setDownloadState] = useState<DownloadState>({
    isDownloading: false,
    isValidating: false,
    isFetching: false,
    progress: new Map(),
    results: [],
    error: null,
    fetchedRides: [],
    importedLayerName: null,
  });

  const startDownload = useCallback(async (
    selectedRideIds: Set<string>,
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkId: number | null;
      startDate: string;
      endDate: string;
      timezone?: string;
      downloadAcknowledged: boolean;
      environment: Environment;
      context?: Context; // Optional context for map layer integration
    }
  ): Promise<void> => {
    if (selectedRideIds.size === 0) {
      return;
    }

    // Extract context and environment from prerequisites
    const { context, environment, ...restPrerequisites } = prerequisites;

    setDownloadState(prev => ({
      ...prev,
      isDownloading: true,
      isFetching: true,
      error: null,
      results: [],
      fetchedRides: [],
    }));

    try {
      // Initialize progress
      const progressMap = new Map<string, RideDownloadProgress>();
      selectedRideIds.forEach(rideId => {
        progressMap.set(rideId, {
          rideId,
          rideName: `Ride ${rideId.substring(0, 8)}...`,
          status: 'pending',
          message: 'Waiting to download...',
        });
      });
      setDownloadState(prev => ({ ...prev, progress: progressMap }));

      // Step 1: Fetch rides list
      setDownloadState(prev => {
        const newProgress = new Map(prev.progress);
        selectedRideIds.forEach(rideId => {
          const existing = newProgress.get(rideId);
          if (existing) {
            newProgress.set(rideId, {
              ...existing,
              status: 'fetching',
              message: 'Fetching ride data...',
              progress: 10,
            });
          }
        });
        return { ...prev, progress: newProgress, isFetching: true };
      });

      // Fetch all rides in the date range (we'll filter by selected IDs later)
      const ridesResponse = await fetchRides(
        restPrerequisites.jwtToken,
        restPrerequisites.productId,
        restPrerequisites.startDate,
        restPrerequisites.endDate,
        0, // offset
        1000, // limit - fetch up to 1000 rides
        'departureTime', // sortBy
        'DESC', // sortOrder
        environment
      );

      if (!ridesResponse?.data?.entries) {
        throw new Error('No rides found in response');
      }

      // Filter to selected rides
      const selectedRideEntries = ridesResponse.data.entries.filter(r => 
        selectedRideIds.has(r.id)
      );

      if (selectedRideEntries.length === 0) {
        throw new Error('No selected rides found in fetched data');
      }

      // Step 2: Fetch detailed information for selected rides
      setDownloadState(prev => {
        const newProgress = new Map(prev.progress);
        selectedRideIds.forEach(rideId => {
          const existing = newProgress.get(rideId);
          if (existing) {
            newProgress.set(rideId, {
              ...existing,
              status: 'fetching',
              message: 'Fetching detailed ride information...',
              progress: 30,
            });
          }
        });
        return { ...prev, progress: newProgress };
      });

      const detailedRides = await fetchRidesDetailedBatch(
        restPrerequisites.jwtToken,
        restPrerequisites.productId,
        selectedRideEntries.map(r => r.id),
        50, // batch size
        (current, total) => {
          // Update progress during batch fetch
          const progressPercent = 30 + (current / total) * 20; // 30-50%
          setDownloadState(prev => {
            const newProgress = new Map(prev.progress);
            selectedRideIds.forEach(rideId => {
              const existing = newProgress.get(rideId);
              if (existing) {
                newProgress.set(rideId, {
                  ...existing,
                  progress: progressPercent,
                  message: `Fetching details: ${current} of ${total}...`,
                });
              }
            });
            return { ...prev, progress: newProgress };
          });
        },
        environment
      );

      // Update progress: extracting
      setDownloadState(prev => {
        const newProgress = new Map(prev.progress);
        selectedRideIds.forEach(rideId => {
          const existing = newProgress.get(rideId);
          if (existing) {
            newProgress.set(rideId, {
              ...existing,
              status: 'importing',
              message: 'Extracting ride data...',
              progress: 50,
            });
          }
        });
        return { ...prev, progress: newProgress, isFetching: false, fetchedRides: detailedRides };
      });

      // Step 3: Extract rides to RideSchema[]
      const ridesResponseForExtraction: { entries: (UATRideEntry | UATRideDetailedEntry)[] } = {
        entries: detailedRides,
      };
      
      // Pass timezone to convert UTC times to network timezone before storing
      const rideSchemas = extractUATRides(ridesResponseForExtraction, restPrerequisites.timezone);

      if (rideSchemas.length === 0) {
        throw new Error('No valid rides to import (missing coordinates or segments)');
      }

      // Step 4: Import rides into RidesContext
      setDownloadState(prev => {
        const newProgress = new Map(prev.progress);
        Array.from(selectedRideIds).forEach((rideId, index) => {
          const existing = newProgress.get(rideId);
          if (existing) {
            const progressPercent = 60 + (index / selectedRideIds.size) * 30; // 60-90%
            newProgress.set(rideId, {
              ...existing,
              status: 'importing',
              message: 'Importing ride...',
              progress: progressPercent,
            });
          }
        });
        return { ...prev, progress: newProgress };
      });

      // Step 4: Add rides to RidesContext
      bulkAddRides(rideSchemas, 'merge');

      // Step 5: Add rides to map layers if context is provided
      if (context) {
        try {
          // Generate layer name: "{Environment} Rides - {Date}"
          const envDisplayName = getEnvironmentDisplayName(environment);
          const dateStr = new Date().toLocaleDateString();
          const layerName = `${envDisplayName} Rides - ${dateStr}`;
          
          setDownloadState(prev => {
            const newProgress = new Map(prev.progress);
            selectedRideIds.forEach(rideId => {
              const existing = newProgress.get(rideId);
              if (existing) {
                newProgress.set(rideId, {
                  ...existing,
                  status: 'importing',
                  message: 'Adding to map...',
                  progress: 90,
                });
              }
            });
            return { ...prev, progress: newProgress };
          });

          const importResults = importUATRides(rideSchemas, context, layerName);
          console.log(`[useDownloadRides] Added ${importResults.length} ride${importResults.length !== 1 ? 's' : ''} to map layer: ${layerName}`);
          
          // Store layer name for auto-selection
          setDownloadState(prev => ({
            ...prev,
            importedLayerName: layerName,
          }));
        } catch (mapError) {
          console.error('[useDownloadRides] Error adding rides to map:', mapError);
          // Don't fail the entire download if map integration fails
          // Rides are already in RidesContext, which is the primary storage
        }
      }

      // Step 6: Mark all as success
      const results: RouteOperationResult[] = [];
      selectedRideIds.forEach(rideId => {
        const ride = rideSchemas.find(r => 
          (r.ride_id || r.external_reference_id) === rideId
        );
        const rideName = ride 
          ? `${ride.from_address || 'Pickup'} → ${ride.to_address || 'Dropoff'}`
          : `Ride ${rideId.substring(0, 8)}...`;

        setDownloadState(prev => {
          const newProgress = new Map(prev.progress);
          newProgress.set(rideId, {
            rideId,
            rideName,
            status: 'success',
            message: 'Ride downloaded successfully',
            progress: 100,
          });
          return { ...prev, progress: newProgress };
        });

        results.push({
          routeId: rideId,
          routeName: rideName,
          success: true,
        });
      });

      setDownloadState(prev => ({
        ...prev,
        isDownloading: false,
        results,
      }));
    } catch (error) {
      const errorMessage = handleApiError(error).message;
      
      // Mark all as error
      setDownloadState(prev => {
        const newProgress = new Map(prev.progress);
        selectedRideIds.forEach(rideId => {
          const existing = newProgress.get(rideId);
          if (existing) {
            newProgress.set(rideId, {
              ...existing,
              status: 'error',
              message: errorMessage,
            });
          }
        });
        return {
          ...prev,
          isDownloading: false,
          isFetching: false,
          error: errorMessage,
          progress: newProgress,
        };
      });
    }
  }, [bulkAddRides]);

  const resetDownload = useCallback(() => {
    setDownloadState({
      isDownloading: false,
      isValidating: false,
      isFetching: false,
      progress: new Map(),
      results: [],
      error: null,
      fetchedRides: [],
    });
  }, []);

  return {
    downloadState,
    startDownload,
    resetDownload,
  };
}

